import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Webhook, Trash2, Plus, TestTube, CheckCircle, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

const AVAILABLE_EVENTS = [
  { value: 'deal.created', label: 'Deal Created' },
  { value: 'deal.approved', label: 'Deal Approved' },
  { value: 'deal.rejected', label: 'Deal Rejected' },
  { value: 'deal.updated', label: 'Deal Updated' },
  { value: 'athlete.created', label: 'Athlete Created' },
  { value: 'athlete.updated', label: 'Athlete Updated' }
];

export default function WebhookManagement() {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);

  const queryClient = useQueryClient();

  const { data: webhooks, isLoading } = useQuery({
    queryKey: ['webhooks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhooks')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  const { data: deliveryLogs } = useQuery({
    queryKey: ['webhook-delivery-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_delivery_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data;
    }
  });

  const createWebhookMutation = useMutation({
    mutationFn: async () => {
      const secret = crypto.randomUUID();
      
      const { data, error } = await supabase
        .from('webhooks')
        .insert({
          url: webhookUrl,
          secret,
          events: selectedEvents,
          is_active: true
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setWebhookUrl("");
      setSelectedEvents([]);
      setShowCreateDialog(false);
      toast.success("Webhook created successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to create webhook: ${error.message}`);
    }
  });

  const toggleWebhookMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('webhooks')
        .update({ is_active: !isActive })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      toast.success("Webhook status updated");
    }
  });

  const deleteWebhookMutation = useMutation({
    mutationFn: async (webhookId: string) => {
      const { error } = await supabase
        .from('webhooks')
        .delete()
        .eq('id', webhookId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      toast.success("Webhook deleted successfully");
    }
  });

  const testWebhookMutation = useMutation({
    mutationFn: async (webhookId: string) => {
      const { data, error } = await supabase.functions.invoke('webhook-dispatcher', {
        body: {
          event: 'test.ping',
          data: {
            id: crypto.randomUUID(),
            message: 'Test webhook delivery',
            timestamp: new Date().toISOString()
          }
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Test webhook sent");
      queryClient.invalidateQueries({ queryKey: ['webhook-delivery-logs'] });
    }
  });

  const getLogsForWebhook = (webhookId: string) => {
    if (!deliveryLogs) return [];
    return deliveryLogs.filter(log => log.webhook_id === webhookId).slice(0, 5);
  };

  return (
    <div className="container mx-auto py-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold">Webhook Management</h1>
          <p className="text-muted-foreground mt-2">
            Configure webhooks to receive real-time event notifications
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Webhook</DialogTitle>
              <DialogDescription>
                Register a webhook URL to receive event notifications
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="webhookUrl">Webhook URL</Label>
                <Input
                  id="webhookUrl"
                  placeholder="https://your-app.com/webhooks/athletenexus"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Must be a valid HTTPS URL
                </p>
              </div>

              <div>
                <Label>Events to Subscribe</Label>
                <div className="space-y-2 mt-2">
                  {AVAILABLE_EVENTS.map(event => (
                    <div key={event.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={event.value}
                        checked={selectedEvents.includes(event.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedEvents([...selectedEvents, event.value]);
                          } else {
                            setSelectedEvents(selectedEvents.filter(e => e !== event.value));
                          }
                        }}
                      />
                      <label htmlFor={event.value} className="text-sm cursor-pointer">
                        {event.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-semibold text-sm mb-2">🔒 Security</h4>
                <p className="text-xs text-muted-foreground">
                  All webhook payloads are signed with HMAC-SHA256. You'll receive a secret 
                  key to verify the authenticity of requests.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => createWebhookMutation.mutate()}
                disabled={!webhookUrl || selectedEvents.length === 0}
              >
                Create Webhook
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6">
        {isLoading ? (
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-muted-foreground">Loading webhooks...</p>
            </CardContent>
          </Card>
        ) : webhooks && webhooks.length > 0 ? (
          webhooks.map((webhook) => {
            const logs = getLogsForWebhook(webhook.id);
            const successRate = logs.length > 0 
              ? (logs.filter(l => l.status_code && l.status_code >= 200 && l.status_code < 300).length / logs.length) * 100
              : 0;
            
            return (
              <Card key={webhook.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2">
                        <Webhook className="h-5 w-5" />
                        {webhook.url}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Created {new Date(webhook.created_at).toLocaleDateString()}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={webhook.is_active}
                        onCheckedChange={() => toggleWebhookMutation.mutate({ 
                          id: webhook.id, 
                          isActive: webhook.is_active 
                        })}
                      />
                      <Badge variant={webhook.is_active ? 'default' : 'secondary'}>
                        {webhook.is_active ? 'Active' : 'Disabled'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Events</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {webhook.events.map((event: string) => (
                        <Badge key={event} variant="outline">{event}</Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Secret (for HMAC verification)</Label>
                    <code className="block p-2 bg-muted rounded text-xs font-mono mt-1">
                      {webhook.secret}
                    </code>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Recent Deliveries</Label>
                    {logs.length > 0 ? (
                      <div className="space-y-2 mt-2">
                        {logs.map((log) => (
                          <div key={log.id} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                            <div className="flex items-center gap-2">
                              {log.status_code && log.status_code >= 200 && log.status_code < 300 ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-600" />
                              )}
                              <span className="font-medium">{log.event_type}</span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <span>{log.status_code || 'Failed'}</span>
                              <span>•</span>
                              <span>{new Date(log.created_at).toLocaleTimeString()}</span>
                            </div>
                          </div>
                        ))}
                        <div className="text-xs text-muted-foreground">
                          Success rate: {successRate.toFixed(1)}%
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground mt-2">No deliveries yet</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-sm text-muted-foreground">
                      Retry: {webhook.retry_count}x, Timeout: {webhook.timeout_seconds}s
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => testWebhookMutation.mutate(webhook.id)}
                      >
                        <TestTube className="h-4 w-4 mr-2" />
                        Test
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteWebhookMutation.mutate(webhook.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Webhook className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No Webhooks Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first webhook to receive real-time event notifications
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Webhook
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
